import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Users, X } from 'lucide-react';
import VideoGrid from '@/components/video-grid/VideoGrid';
import MediaControls from '@/components/media-controls/MediaControls';
import { useToast } from '@/hooks/use-toast';
import { useRoomCall } from '@/hooks/useRoomCall';

const connectionTone = (state) => {
  if (state === 'connected') {
    return 'bg-emerald-50 text-emerald-700 border-emerald-100';
  }
  if (state === 'reconnecting') {
    return 'bg-amber-50 text-amber-700 border-amber-100';
  }
  return 'bg-slate-100 text-slate-600 border-slate-200';
};

const VideoCallModal = ({
  open,
  room,
  call,
  api,
  user,
  socket,
  realtimeState,
  onClose,
}) => {
  const { toast } = useToast();
  const {
    participants,
    participantCount,
    connectionState,
    microphoneEnabled,
    cameraEnabled,
    screenShareEnabled,
    screenShareSupported,
    connectToCall,
    disconnect,
    toggleMicrophone,
    toggleCamera,
    toggleScreenShare,
  } = useRoomCall();

  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);
  const [controlBusy, setControlBusy] = useState(false);
  const joinedRef = useRef(false);

  const emitSocketEvent = useCallback((type, payload = {}) => {
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    socket.send(JSON.stringify({ type, payload }));
  }, [socket]);

  const leaveCall = useCallback(async ({ silent = false } = {}) => {
    if (call?.call_id && joinedRef.current) {
      emitSocketEvent('call.left', { call_id: call.call_id });
    }

    joinedRef.current = false;
    setJoined(false);
    await disconnect();

    if (!silent) {
      onClose();
    }
  }, [call?.call_id, disconnect, emitSocketEvent, onClose]);

  useEffect(() => {
    joinedRef.current = joined;
  }, [joined]);

  const runControlAction = useCallback(async (action, fallbackMessage) => {
    try {
      setControlBusy(true);
      await action();
    } catch (error) {
      toast({
        title: 'Call control unavailable',
        description: error?.message || fallbackMessage,
        variant: 'destructive',
      });
    } finally {
      setControlBusy(false);
    }
  }, [toast]);

  useEffect(() => {
    if (!open || !room?.room_id || !call?.call_id) return undefined;

    let cancelled = false;

    const joinCall = async () => {
      try {
        setJoining(true);
        const response = await api.get('/get-token', {
          params: {
            username: user?.name || 'Participant',
            room: room.room_id,
            call_id: call.call_id,
          },
        });

        if (cancelled) return;

        const result = await connectToCall({
          url: response.data.url,
          token: response.data.token,
        });

        if (cancelled) {
          await disconnect();
          return;
        }

        joinedRef.current = true;
        setJoined(true);
        emitSocketEvent('call.joined', {
          call_id: response.data.call_id || call.call_id,
        });

        if (result.warnings?.length) {
          toast({
            title: 'Joined with limited media access',
            description: result.warnings.join('. '),
          });
        }
      } catch (error) {
        const description = error.response?.data?.detail || error.message || 'Failed to join the call';
        toast({
          title: 'Unable to join call',
          description,
          variant: 'destructive',
        });
        onClose();
      } finally {
        if (!cancelled) {
          setJoining(false);
        }
      }
    };

    joinCall();

    return () => {
      cancelled = true;
      leaveCall({ silent: true }).catch(() => {});
    };
  }, [api, call?.call_id, connectToCall, disconnect, emitSocketEvent, leaveCall, onClose, open, room?.room_id, toast, user?.name]);

  useEffect(() => {
    if (!open || !joined || !call?.call_id || realtimeState !== 'live') return undefined;

    emitSocketEvent('call.joined', { call_id: call.call_id });
    const intervalId = window.setInterval(() => {
      emitSocketEvent('call.heartbeat', { call_id: call.call_id });
    }, 20000);

    return () => window.clearInterval(intervalId);
  }, [call?.call_id, emitSocketEvent, joined, open, realtimeState]);

  useEffect(() => {
    if (!open || !call?.status || call.status === 'active') return;
    toast({
      title: 'Call ended',
      description: 'This room call is no longer active.',
    });
    onClose();
  }, [call?.status, onClose, open, toast]);

  if (!open || !call) {
    return null;
  }

  const totalParticipants = Math.max(
    participantCount,
    call.participant_count || 0,
    joined ? 1 : 0,
  );

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
      <div className="flex h-[92vh] w-full max-w-7xl flex-col overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_32px_120px_rgba(15,23,42,0.32)]">
        <header className="border-b border-slate-100 px-5 py-4 md:px-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0">
              <h2 className="truncate text-xl font-bold text-slate-900">{room?.title || 'Room Call'}</h2>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                  <Users className="h-3.5 w-3.5" />
                  {totalParticipants} participant{totalParticipants === 1 ? '' : 's'}
                </span>
                <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold capitalize ${connectionTone(connectionState)}`}>
                  {joining ? 'Connecting' : connectionState}
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => leaveCall().catch(() => {})}
              className="inline-flex items-center justify-center rounded-full border border-slate-200 p-2 text-slate-600 transition hover:bg-slate-50"
              aria-label="Leave call"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </header>

        <div className="min-h-0 flex-1 bg-[linear-gradient(180deg,#f8fafc_0%,#eef5f5_100%)] p-4 md:p-6">
          {joining ? (
            <div className="flex h-full min-h-[360px] flex-col items-center justify-center rounded-[2rem] border border-dashed border-slate-200 bg-white/70 text-center">
              <Loader2 className="h-10 w-10 animate-spin text-[#1a5c5c]" />
              <p className="mt-4 text-base font-semibold text-slate-800">Connecting to the call</p>
              <p className="mt-2 text-sm text-slate-500">
                LiveKit is preparing your audio and video session.
              </p>
            </div>
          ) : (
            <VideoGrid participants={participants} />
          )}
        </div>

        <footer className="border-t border-slate-100 bg-white px-5 py-4 md:px-6">
          <MediaControls
            microphoneEnabled={microphoneEnabled}
            cameraEnabled={cameraEnabled}
            screenShareEnabled={screenShareEnabled}
            screenShareSupported={screenShareSupported}
            busy={controlBusy || joining}
            onToggleMicrophone={() => runControlAction(toggleMicrophone, 'Unable to update microphone')}
            onToggleCamera={() => runControlAction(toggleCamera, 'Unable to update camera')}
            onToggleScreenShare={() => runControlAction(toggleScreenShare, 'Unable to update screen sharing')}
            onLeave={() => leaveCall().catch(() => {})}
          />
        </footer>
      </div>
    </div>
  );
};

export default VideoCallModal;
